import { Outlet, createFileRoute, getRouteApi } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import z from 'zod';

import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { AccessTokensPageContent } from '@/components/PersonalAccessTokens/AccessTokensPageContent';
import { SettingsPageLayout } from '@/components/Settings/SettingsPageLayout';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';

const accessTokensSearchSchema = z.object({
  tab: fallback(z.enum(['personal', 'machines']), 'personal').default('personal'),
  pPageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  pPageIndex: fallback(z.number(), 0).default(0),
  pSearch: searchParam().optional(),
  pStatus: z.enum(['active', 'expired']).optional().catch(undefined),
  pNew: fallback(z.boolean(), false).default(false),
  mPageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  mPageIndex: fallback(z.number(), 0).default(0),
  mSearch: searchParam().optional(),
  mNew: fallback(z.boolean(), false).default(false),
});

export const Route = createFileRoute('/$organizationName/settings/access-tokens')({
  component: AccessTokensPage,
  validateSearch: zodValidator(accessTokensSearchSchema),
});

const accessTokensRouteApi = getRouteApi('/$organizationName/settings/access-tokens');

function AccessTokensPage() {
  const { organizationDisplayName } = useUser();
  const search = accessTokensRouteApi.useSearch();
  const navigate = accessTokensRouteApi.useNavigate();
  const { tab } = search;

  const effectiveTab = tab;

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Access Tokens`;
  }, [organizationDisplayName]);

  const handleTabChange = (newTab: string) => {
    const validTab = newTab as 'personal' | 'machines';
    navigate({
      search: (prev) => ({ ...prev, tab: validTab }),
    });
  };

  return (
    <SettingsPageLayout
      fullWidthWithPadding
      newHeader
      hideSeparator
      title="Access Tokens"
      subTitle="Manage personal access tokens and machine tokens for programmatic access to Driver."
    >
      <AccessTokensPageContent effectiveTab={effectiveTab} onTabChange={handleTabChange} />
      <Outlet />
    </SettingsPageLayout>
  );
}
